import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LibraryQuery } from '../../api/centerstageApi';
import { useClientContext } from '../../hooks/useClientContext';

/** The Centerstage page's committed filters, as held in the query string. */
export interface CenterstageFilterValues {
  /** The committed search term ('' when none). */
  q: string;
  categoryId: number | null;
  conceptId: number | null;
  type: string | null;
  setting: string | null;
}

export type CenterstageFilterPatch = Partial<CenterstageFilterValues>;

const PARAM: Record<keyof CenterstageFilterValues, string> = {
  q: 'q',
  categoryId: 'category',
  conceptId: 'concept',
  type: 'type',
  setting: 'setting',
};

const positiveId = (value: string | null): number | null =>
  value !== null && /^[1-9]\d*$/.test(value) ? Number(value) : null;

const text = (value: string | null): string | null => (value && value.trim() ? value.trim() : null);

export function readCenterstageFilters(params: URLSearchParams): CenterstageFilterValues {
  return {
    q: text(params.get(PARAM.q)) ?? '',
    categoryId: positiveId(params.get(PARAM.categoryId)),
    conceptId: positiveId(params.get(PARAM.conceptId)),
    type: text(params.get(PARAM.type)),
    setting: text(params.get(PARAM.setting)),
  };
}

/**
 * Filter state for the Centerstage page, held in the query string (`q`,
 * `category`, `concept`, `type`, `setting`) so a filtered view can be linked
 * and survives reload. Every change pushes a history entry, so browser back
 * steps back through filter changes. Switching the content context clears
 * every filter (replacing the entry, so back does not restore them).
 */
export function useCenterstageFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readCenterstageFilters(searchParams), [searchParams]);

  const update = useCallback((patch: CenterstageFilterPatch) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch) as Array<[keyof CenterstageFilterValues, CenterstageFilterValues[keyof CenterstageFilterValues]]>) {
        if (value === null || value === undefined || value === '') next.delete(PARAM[key]);
        else next.set(PARAM[key], String(value));
      }
      return next;
    });
  }, [setSearchParams]);

  const clearAll = useCallback(() => setSearchParams(new URLSearchParams()), [setSearchParams]);

  const { clientContext } = useClientContext();
  const contextId = clientContext?.id ?? null;
  const previousContextId = useRef(contextId);
  useEffect(() => {
    if (previousContextId.current === contextId) return;
    previousContextId.current = contextId;
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [contextId, setSearchParams]);

  const query: LibraryQuery = useMemo(() => ({
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.categoryId !== null ? { categoryId: filters.categoryId } : {}),
    ...(filters.conceptId !== null ? { conceptId: filters.conceptId } : {}),
    ...(filters.type !== null ? { type: filters.type } : {}),
    ...(filters.setting !== null ? { setting: filters.setting } : {}),
  }), [filters]);

  const hasFilters = Boolean(filters.q) || filters.categoryId !== null || filters.conceptId !== null
    || filters.type !== null || filters.setting !== null;

  return { filters, query, hasFilters, update, clearAll };
}
